import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import CommonWrapper from '../../../components/ui_components/AdminWrapper';
import colors from '../../../styles/colors';
import { apiGetCardAccounts } from '../../../api/userApi';
import type { CardAccount } from '../../../types/models/userCredit';
import CreditModal from './CreditModal';
import InventoryModal from './InventoryModal';
import GrantAllCreditsModal from './GrantAllCreditsModal';

const PageInner = styled.div`
  padding: 32px 24px;
  max-width: 1100px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: gold;
  margin: 0;
  flex: 1;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  background-color: #1e1e1e;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  color: #fff;
  font-size: 14px;
  margin-bottom: 20px;
  outline: none;
  &::placeholder { color: rgba(255, 255, 255, 0.35); }
  &:focus { border-color: rgba(255, 215, 0, 0.4); }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 14px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const Tr = styled.tr`
  &:hover td { background-color: rgba(255, 255, 255, 0.03); }
`;

const Td = styled.td`
  padding: 12px 14px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  vertical-align: middle;
`;

const Credits = styled.span`
  font-weight: 700;
  color: #fff;
  font-size: 15px;
`;

const CreditBtn = styled.button`
  padding: 6px 14px;
  background-color: transparent;
  color: gold;
  border: 1px solid gold;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background-color: rgba(255, 215, 0, 0.12); }
`;

const GrantAllBtn = styled.button`
  padding: 8px 18px;
  background-color: rgba(255, 215, 0, 0.1);
  color: gold;
  border: 1px solid gold;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background-color: rgba(255, 215, 0, 0.2); }
`;

const StatusText = styled.p`
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
  margin-top: 40px;
`;

const ErrorText = styled.p`
  color: #dc3545;
  text-align: center;
  margin-top: 40px;
`;

const UserListPage: React.FC = () => {
  const [accounts, setAccounts] = useState<CardAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedAccount, setSelectedAccount] = useState<CardAccount | null>(null);
  const [inventoryAccount, setInventoryAccount] = useState<CardAccount | null>(null);
  const [grantAllOpen, setGrantAllOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;
    apiGetCardAccounts()
      .then((res) => { if (isMounted) setAccounts(res.data); })
      .catch(() => { if (isMounted) setError('Errore durante il caricamento degli account.'); })
      .finally(() => { if (isMounted) setIsLoading(false); });
    return () => { isMounted = false; };
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return accounts;
    const q = search.toLowerCase();
    return accounts.filter(
      (a) =>
        a.user.username.toLowerCase().includes(q) ||
        (a.user.nickname_forum?.toLowerCase().includes(q) ?? false)
    );
  }, [accounts, search]);

  const handleCreditsUpdated = (accountId: number, delta: number) => {
    setAccounts((prev) =>
      prev.map((a) => (a.id === accountId ? { ...a, credits: a.credits + delta } : a))
    );
  };

  const handleGrantedAll = (amount: number) => {
    setAccounts((prev) => prev.map((a) => ({ ...a, credits: a.credits + amount })));
  };

  return (
    <>
      <CommonWrapper>
        <PageInner>
          <PageHeader>
            <PageTitle>Gestisci Crediti Utenti</PageTitle>
            <GrantAllBtn onClick={() => setGrantAllOpen(true)}>
              Distribuisci a tutti
            </GrantAllBtn>
          </PageHeader>

          <SearchInput
            placeholder="Cerca per username o nickname..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {isLoading && <StatusText>Caricamento...</StatusText>}
          {error && <ErrorText>{error}</ErrorText>}

          {!isLoading && !error && (
            <Table>
              <thead>
                <tr>
                  <Th>Username</Th>
                  <Th>Nickname</Th>
                  <Th>Crediti</Th>
                  <Th></Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <Tr>
                    <Td colSpan={4} style={{ textAlign: 'center', color: 'rgba(255,255,255,0.35)' }}>
                      Nessun account trovato.
                    </Td>
                  </Tr>
                )}
                {filtered.map((a) => (
                  <Tr key={a.id}>
                    <Td><strong>{a.user.username}</strong></Td>
                    <Td style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>{a.user.nickname_forum ?? '—'}</Td>
                    <Td><Credits>{(a.credits ?? 0).toLocaleString()}</Credits></Td>
                    <Td>
                      <CreditBtn onClick={() => setSelectedAccount(a)}>± Crediti</CreditBtn>
                    </Td>
                    <Td>
                      <CreditBtn onClick={() => setInventoryAccount(a)}>Inventario</CreditBtn>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </PageInner>
      </CommonWrapper>

      {selectedAccount && (
        <CreditModal
          account={selectedAccount}
          onClose={() => setSelectedAccount(null)}
          onCreditsUpdated={handleCreditsUpdated}
        />
      )}

      {inventoryAccount && (
        <InventoryModal
          account={inventoryAccount}
          onClose={() => setInventoryAccount(null)}
        />
      )}

      {grantAllOpen && (
        <GrantAllCreditsModal
          onClose={() => setGrantAllOpen(false)}
          onGranted={handleGrantedAll}
        />
      )}
    </>
  );
};

export default UserListPage;
